import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LazyVideoPreview from "./LazyVideoPreview";
import { List, RowComponentProps } from "react-window";


export type MediaEntry = {
  kind: 'image' | 'video';

  // image
  url?: string;
 
  // new video pipeline
  isHls?: boolean;
  videoId?: string | null;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // metadata
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};
    
    
type Usage = {
  postId: string;
  url: string;
  musicTitle: string | null;
  musicArtist: string | null;
  filterId: string | null;
  createdAt: string;
  userId?: string;
  ownerId?: string;
  userDetails?: { // Update `user` to `userDetails`
    _id: string;
    profilename?: string;
    picture?: string | null;
  } | null;
  location?: {
    city?: string;
    country?: string;
    countryCode?: string;
  };
  likes?: number;
  likedByUser?: boolean;
  media?: MediaEntry[]; // Explicitly declare media as an array of MediaEntry
};


type Track = {
  _id: string;
  title: string;
  artist?: string | null;
  duration?: number | null;
  coverUrl?: string | null;
  audioUrl?: string | null;
};

type MusicRowProps = {
  rows: Usage[][];
  navigate: ReturnType<typeof useNavigate>;
  authUserId?: string;
  musicId?: string;
  track: Track | null;
  activeIndex: number;
  t: (key: string, options?: any) => string;
};


interface MediaItemProps {
  media: MediaEntry;
  track?: Track;
  isActive: boolean;
  videoId: string;
}
    
// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

const resolveVideoUrl = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  if (m.url && /^https?:\/\//i.test(m.url)) return m.url; // legacy fallback
  return '';
};

const mediaHeight = 320;
const COLUMN_COUNT = 2;
const ROW_HEIGHT = mediaHeight + 95; // adjust if needed



export function MediaItem({
  media,
  track,
  isActive,
  videoId,
}: MediaItemProps) {
  const isVideo =
    media.kind === 'video' &&
    !!(media.hlsUrl || media.videoId || media.url);

  const mediaSrc = isVideo ? resolveVideoUrl(media) : (media.url || '');
  const posterSrc = media.posterUrl || track?.coverUrl || undefined;

  if (isVideo && mediaSrc) {
    return (
      <LazyVideoPreview
        src={mediaSrc}
        poster={posterSrc}
        videoId={videoId}
        active={isActive}
        style={styles.previewVideo}
      />
    );
  }

  return (
    <img
      src={mediaSrc}
      alt="Preview"
      style={styles.previewImage}
      loading="lazy"
      decoding="async"
    />
  );
}



function MusicUsageRow({
  index,
  style,
  rows,
  navigate,
  authUserId,
  musicId,
  track,
  activeIndex,
  t,
}: RowComponentProps<MusicRowProps>) {
  const rowItems = rows[index];

  return (
    <div
      style={{
        ...style,
        display: 'grid',
        gridTemplateColumns: `repeat(${COLUMN_COUNT}, 1fr)`,
        gap: 20,
        width: '100%',
        maxWidth: 900,
        margin: '0 auto',
        boxSizing: 'border-box',
        paddingBottom: 14,
        alignItems: 'start',
      }}
    >
      {rowItems.map((u, colIndex) => {
        const usageIndex = index * COLUMN_COUNT + colIndex;

        const user = u.userDetails;
        const userDetails = u.userDetails;
        const userName = user?.profilename || t('gallery.post.unknownUser');
        const userHasPic =
          typeof user?.picture === 'string' && user.picture !== '';

        const userPicStyle = userHasPic
          ? {
              backgroundImage: `url(${user.picture})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              fontSize: 0,
            }
          : {
              backgroundColor: '#261da2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            };

        const media = Array.isArray(u.media) && u.media.length > 0 ? u.media[0] : null;

        return (
          <div key={u.postId} style={styles.pin}>
            <div style={styles.pinMeta}>
              <div style={styles.userLinkWrapper}>
                <div
                  onClick={() => {
                    if (!userDetails?._id) return;
                    const isOwnProfile = authUserId === userDetails._id;
                    navigate(isOwnProfile ? '/profile' : `/visitor-profile/${userDetails._id}`);
                  }}
                  style={{ ...styles.userLink, cursor: 'pointer' }}
                  title={userName}
                >
                  <div style={{ ...styles.userPic, ...userPicStyle }}>
                    {!userHasPic && '👻'}
                  </div>
                  <span>{userName}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  if (!musicId) return;
                  navigate(`/musicdetails/${musicId}/post/${u.postId}`);
                }}
                style={styles.pinLink}
              >
                {t('musicDetail.openPostLabel')}
              </button>
            </div>

            <div style={styles.mediaWrap}>
              {media ? (
                <MediaItem
                  media={media}
                  track={track || undefined}
                  isActive={usageIndex === activeIndex}
                  videoId={`${u.postId}-preview`}
                />
              ) : (
                <div>{t('gallery.post.noMedia')}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}



export default function MusicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const [track, setTrack] = useState<Track | null>(null);
  const [usages, setUsages] = useState<Usage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  //Loading
  const [offset, setOffset] = useState(0);
const LIMIT = 20;
const [hasMore, setHasMore] = useState(true);
const [loadingMore, setLoadingMore] = useState(false);

const loadingRef = React.useRef(false);

const MAX_ITEMS = 300;

const [listHeight, setListHeight] = useState(
  Math.max(300, window.innerHeight - 170)
);

useEffect(() => {
  const onResize = () => {
    setListHeight(Math.max(300, window.innerHeight - 170));
  };

  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);




const loadMore = async () => {
  if (!id || !hasMore || loadingMore || loadingRef.current) return;

  loadingRef.current = true;
  setLoadingMore(true);

  try {
    const res = await api.get(`/music/${id}/usages-with-users`, {
      params: { offset, limit: LIMIT }
    });

    const newUsages = Array.isArray(res.data?.usages) ? res.data.usages : [];

    setUsages(prev => {
      const map = new Map(prev.map((p) => [p.postId, p]));

      for (const item of newUsages) {
        if (!item?.postId) continue;
        map.set(item.postId, item);
      }

      const merged = Array.from(map.values());
      return merged.slice(-MAX_ITEMS);
    });

    setOffset(prev => prev + newUsages.length);
    setHasMore(newUsages.length === LIMIT);
  } catch (e) {
    console.error('Music pagination load failed:', e);
  } finally {
    loadingRef.current = false;
    setLoadingMore(false);
  }
};


const rows = useMemo(() => {
  const chunked: Usage[][] = [];
  for (let i = 0; i < usages.length; i += COLUMN_COUNT) {
    chunked.push(usages.slice(i, i + COLUMN_COUNT));
  }
  return chunked;
}, [usages]);





  useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);
  return () => style.remove();
}, []);

//Fetching Music Posts
useEffect(() => {
  if (!id) return;

  setTrack(null);
  setUsages([]);
  setOffset(0);
  setHasMore(true);
  setLoading(true);
  setActiveIndex(0);

  (async () => {
      try {
const res = await api.get(`/music/${id}/usages-with-users`, {
  params: { offset: 0, limit: LIMIT }
});

const initialUsages = Array.isArray(res.data?.usages) ? res.data.usages : [];

setTrack(res.data?.track ?? null);
setUsages(initialUsages.slice(-MAX_ITEMS));
setOffset(initialUsages.length);
setHasMore(initialUsages.length === LIMIT);

      } catch (e) {
        console.error('Error fetching data:', e);
        alert(t('musicDetail.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // Dependency on `id` only



if (loading) {
  return (
    <div
      style={{
        height: '100dvh',
        width: '100%',
        background: '#050913',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.15)',
          borderTop: '3px solid #fff',
          animation: 'spin 0.9s linear infinite',
        }}
      />
    </div>
  );
}


  if (!track) {
    return (
      <div style={{ padding: 24, color: '#e6e9f3' }}>
        {t('musicDetail.notFound')}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Close button */}
      <button
        onClick={() => navigate(-1)}
        aria-label={t('musicDetail.close')}
        title={t('musicDetail.close')}
        style={styles.closeBtn}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Header */}
      <div style={styles.headerCard}>
        <div style={styles.coverWrap}>
          {track.coverUrl ? (
            <img src={track.coverUrl} alt="Cover" style={styles.coverImg} />
          ) : (
            <div style={styles.coverFallback}>🎵</div>
          )}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={styles.titleRow}>
            <h1 style={styles.title}>{track.title}</h1>
            {typeof track.duration === 'number' && track.duration > 0 && (
              <span style={styles.badge}>{Math.round(track.duration)}s</span>
            )}
          </div>
          <div style={styles.artist}>
            {track.artist || t('musicDetail.unknownArtist')}
          </div>

          {track.audioUrl && (
            <audio controls src={track.audioUrl} style={styles.audio} />
          )}
        </div>
      </div>

      {/* Display posts that use this music */}
      {usages.length === 0 ? (
        <div style={styles.empty}>{t('musicDetail.empty')}</div>
      ) : (


<List
  style={{ height: listHeight, width: '100%' }}
  rowCount={rows.length}
  rowHeight={ROW_HEIGHT}
  rowComponent={MusicUsageRow}
  rowProps={{
    rows,
    navigate,
    authUserId: authUser?._id,
    musicId: id,
    track,
    activeIndex,
    t,
  }}
  overscanCount={3}
  onRowsRendered={({ startIndex, stopIndex }) => {
    const nextActiveIndex = startIndex * COLUMN_COUNT;
    if (nextActiveIndex !== activeIndex) {
      setActiveIndex(nextActiveIndex);
    }

    if (!hasMore || loadingMore || loadingRef.current) return;

    if (stopIndex >= rows.length - 3) {
      loadMore();
    }
  }}
/>

          
      )}

      {loadingMore && (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />
  </div>
)}
    </div>
  );
}



// Styles remain the same as in your previous code


const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    width: '100%',
    background: '#050913',
    color: '#e6e9f3',
    padding: '20px 18px',
    paddingTop: 32,
    boxSizing: 'border-box',
  },

  // Close button: no border now
  closeBtn: {
    position: 'fixed',
    top: 14,
    right: 18,
    zIndex: 60,
    width: 34,
    height: 34,
    display: 'grid',
    placeItems: 'center',
    border: 'none', // <-- removed border
    borderRadius: 999,
    background:
      'radial-gradient(circle at 0% 0%, rgba(255,255,255,0.12), rgba(10,10,12,0.95))',
    color: '#e6e9f3',
    backdropFilter: 'blur(10px)',
    cursor: 'pointer',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
    boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
  },

  // Header card narrower, especially on the right side
  headerCard: {
    display: 'grid',
    gridTemplateColumns: '72px 1fr',
    gap: 14,
    padding: 14,
    borderRadius: 12,
    background:
      'linear-gradient(155deg, rgba(255,255,255,0.06), rgba(10,10,16,0.9))',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)',
    marginBottom: 18,
    maxWidth: 1300,            // <-- limits width, so right side isn't so wide
    marginRight: 'auto',      // align to the left
  },

  coverWrap: {
    width: 72,
    height: 72,
    borderRadius: 12,
    overflow: 'hidden',
    background: '#000',
    display: 'grid',
    placeItems: 'center',
  },
  coverImg: { width: '100%', height: '100%', objectFit: 'cover' },
  coverFallback: { fontSize: 28, opacity: 0.9 },

  titleRow: { display: 'flex', alignItems: 'center', gap: 8 },
  title: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  badge: {
    fontSize: 12,
    color: '#111',
    background: '#ffd76a',
    borderRadius: 999,
    padding: '2px 8px',
    fontWeight: 700,
  },
  artist: { fontSize: 13, color: '#a9b0c6', marginTop: 2 },

  audio: {
    width: '100%',
    maxWidth: 420,
    marginTop: 8,
    filter: 'invert(1)',
    background: 'transparent',
  },


  pinMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '8px 10px',
  },

  pinLink: {
    fontSize: 12,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.06)',
    color: '#e6e9f3',
    padding: '4px 8px',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  userLinkWrapper: {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
},

userLink: {
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  color: '#cfd6ec',
  fontSize: 13,
  fontWeight: 500,
  gap: 8,
},

userPic: {
  width: 30,
  height: 30,
  borderRadius: '5%',
  objectFit: 'cover',
  backgroundColor: '#1a1a1a',
  flexShrink: 0,
},

mediaWrap: {
  position: 'relative',
  width: '100%',
  height: mediaHeight,
  overflow: 'hidden',
  background: '#000',
  borderRadius: 12,
  marginBottom: 6,
},

previewImage: {
  width: '100%',
  height: mediaHeight,
  objectFit: 'cover',
  borderRadius: 12,
  display: 'block',
  background: '#000',
},

previewVideo: {
  width: '100%',
  height: mediaHeight,
  objectFit: 'cover',
  borderRadius: 12,
  display: 'block',
  background: '#000',
},

pin: {
  display: 'inline-block',
  width: '100%',
  marginBottom: 14,
  breakInside: 'avoid',
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
  overflow: 'hidden',
  alignSelf: 'start',
},

countHeading: {
  fontSize: 13,
  color: '#cfd6ec',
  marginTop: 6,
},

  empty: { color: '#9aa3bd' },
};


